/**
 * Skeleton placeholders used while data loads.
 *
 * Each shape mirrors its real-world counterpart closely so the page
 * does not "jump" when real data arrives. All variants use the MUI
 * <Skeleton /> under the hood — they get dark-mode / animation
 * treatment for free.
 */
import React from "react";
import MuiSkeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import "./Skeleton.css";

export function Skeleton({
    width,
    height,
    variant = "text",
    animation = "wave",
    className = "",
    style,
}) {
    return (
        <MuiSkeleton
            variant={variant}
            animation={animation}
            width={width}
            height={height}
            className={`pt-skeleton ${className}`}
            style={style}
        />
    );
}

export function SkeletonText({ lines = 3, lastWidth = "60%" }) {
    return (
        <Stack spacing={1} className="pt-skeleton-text">
            {Array.from({ length: lines }).map((_, i) => (
                <MuiSkeleton
                    key={i}
                    variant="text"
                    height={14}
                    width={i === lines - 1 ? lastWidth : "100%"}
                    animation="wave"
                />
            ))}
        </Stack>
    );
}

export function SkeletonTableRow({ cols = 6, dense = false }) {
    const widths = ["32%", "24%", "18%", "20%", "16%", "20%", "26%", "14%"];
    return (
        <tr className={`pt-skeleton-row ${dense ? "dense" : ""}`}>
            {Array.from({ length: cols }).map((_, i) => (
                <td key={i}>
                    <MuiSkeleton
                        variant="text"
                        height={dense ? 14 : 18}
                        width={widths[i % widths.length]}
                        animation="wave"
                    />
                </td>
            ))}
        </tr>
    );
}

export function SkeletonCard({ height = 160 }) {
    return (
        <Box className="pt-skeleton-card" sx={{ height }}>
            <Stack spacing={1.5}>
                <MuiSkeleton variant="text" width="40%" height={18} animation="wave" />
                <MuiSkeleton variant="rectangular" height={height - 80} animation="wave" />
                <MuiSkeleton variant="text" width="70%" height={14} animation="wave" />
            </Stack>
        </Box>
    );
}

export function SkeletonChart({ height = 260 }) {
    return (
        <Box className="pt-skeleton-card" sx={{ height }}>
            <Stack direction="row" sx={{ height: "100%" }} alignItems="flex-end" spacing={1.5}>
                {[40, 70, 55, 90, 65, 80, 50, 75].map((h, i) => (
                    <MuiSkeleton
                        key={i}
                        variant="rectangular"
                        width="11%"
                        height={`${h}%`}
                        animation="wave"
                        sx={{ borderRadius: 1.5 }}
                    />
                ))}
            </Stack>
        </Box>
    );
}

export function SkeletonMetricCard() {
    return (
        <Box className="pt-skeleton-metric-card">
            <Stack spacing={1.25}>
                <MuiSkeleton variant="text" width="55%" height={12} animation="wave" />
                <MuiSkeleton variant="text" width="40%" height={32} animation="wave" />
                <MuiSkeleton variant="text" width="70%" height={12} animation="wave" />
            </Stack>
        </Box>
    );
}

export function SkeletonHeaderCard() {
    return (
        <Box className="pt-skeleton-header-card">
            <Stack direction="row" spacing={2.5} alignItems="center">
                <MuiSkeleton variant="circular" width={64} height={64} animation="wave" />
                <Stack spacing={1.25} sx={{ flexGrow: 1 }}>
                    <MuiSkeleton variant="text" width="30%" height={28} animation="wave" />
                    <Stack direction="row" spacing={1}>
                        <MuiSkeleton variant="rounded" width={70} height={22} animation="wave" />
                        <MuiSkeleton variant="text" width="22%" height={16} animation="wave" />
                    </Stack>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <MuiSkeleton variant="rounded" width={110} height={40} animation="wave" />
                    <MuiSkeleton variant="rounded" width={110} height={40} animation="wave" />
                </Stack>
            </Stack>
        </Box>
    );
}

export function SkeletonTabs({ count = 3 }) {
    return (
        <Stack direction="row" spacing={1}>
            {Array.from({ length: count }).map((_, i) => (
                <MuiSkeleton
                    key={i}
                    variant="rounded"
                    width={120}
                    height={42}
                    animation="wave"
                />
            ))}
        </Stack>
    );
}

export function SkeletonAvatar({ size = 40 }) {
    return <MuiSkeleton variant="circular" width={size} height={size} animation="wave" />;
}

export default Skeleton;
